import { DrawSystemImpl, System } from 'minecs';
import type { ReadOnlyGameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { PixiViewportSystem } from 'yage/systems/render/PixiViewport';
import type { Handle } from 'yage/audio';
import { getAudio, setAudioEnabled } from 'yage/audio/runtime';
import { AirportState, AirportPlane, AirportStation, AirportPassenger, AirportAgent, AirportStaff, AirportPath, AirportTrash } from '../components/index.js';
import { state, career, stations, ids } from '../services/world.js';
import { LOCAL } from '../services/local.js';
import { projectDirection, unprojectWorld } from '../render/camera.js';
import { AirportActivityAudio, type AudioActor } from '../services/audio-activity.js';
import { AirportDetectorAudio } from '../services/audio-detector.js';
import { onPad, stationPad, SECURITY_GATE } from '../domain/config.js';
import { worksDesk } from '../services/crew.js';
import { PHASE } from '../domain/operations.js';
import { tier } from '../domain/airports.js';
import { hoardingFor } from '../domain/layout.js';
import { FALL, STAGGER } from '../render/opening.js';

@System(AirportState)
export class AirportAudioEffects extends DrawSystemImpl<ReadOnlyGameModel> {
  static depth = 11400;
  private previous: { collected: number; money: number; built: number; upgrades: number; mission: number; hires: number; walkout: number; notice: number; pending: number; flights: number; hoarding: number; plan: string } | undefined;
  private detector = new AirportDetectorAudio();
  private typing = new Map<number, Handle>();
  private activity = new AirportActivityAudio();
  private planes = new Map<number, { phase: number; called: boolean }>();
  private lastCue = new Map<string, number>();
  run = (g: ReadOnlyGameModel) => {
    setAudioEnabled(!LOCAL.muted && !g.paused && !document.hidden);
    const audio = getAudio(), e = state(g);
    if (audio) {
      const center = g.getSystem(PixiViewportSystem).viewport.center;
      const position = unprojectWorld(center.x, center.y);
      // The camera's screen-right floor axis determines headphone left/right after rotation.
      const x = projectDirection(1, 0), y = projectDirection(0, 1);
      audio.setListener({ position: [position.x, 80, position.y], forward: [y.x, -0.7, -x.x], up: [0, 1, 0] });
    }
    const people = ids(g, AirportPassenger).map(id => ({ id, position: g.getTypedUnsafe(Transform, id) }));
    const agents = ids(g, AirportAgent), staff = ids(g, AirportStaff);
    const desks = ids(g, AirportStation).map(id => {
      const station = g.getTypedUnsafe(AirportStation, id), position = g.getTypedUnsafe(Transform, id), work = stationPad(station.index, 'work');
      const workers = agents.filter(actor => onPad('work', g.getTypedUnsafe(Transform, actor), work)).length
        + staff.filter(actor => { const person = g.getTypedUnsafe(AirportStaff, actor); return worksDesk(person) && !person.onBreak && person.training <= 0 && person.stage === station.index && onPad('work', g.getTypedUnsafe(Transform, actor), work); }).length;
      const serving = people.find(person => { const passenger = g.getTypedUnsafe(AirportPassenger, person.id); return passenger.stage === station.index && passenger.state === 2 && passenger.remaining > 0; });
      return { id, index: station.index, unlocked: station.unlocked, workers: serving ? workers : 0, served: station.served, boardingSerial: serving ? g.getTypedUnsafe(AirportPassenger, serving.id).serial : undefined, x: position.x, y: position.y };
    });
    for (const [id, handle] of this.typing) {
      if (!desks.some(desk => desk.id === id && desk.unlocked && desk.workers > 0) || LOCAL.muted || g.paused || document.hidden) { handle.stop(); this.typing.delete(id); }
    }
    const actors: AudioActor[] = agents.map(id => {
      const person = g.getTypedUnsafe(AirportAgent, id), position = g.getTypedUnsafe(Transform, id);
      return { id, x: position.x, y: position.y, activity: person.building >= 0 ? 'apt-construct-hammer' : '' };
    });
    const listener = audio?.listenerPosition ?? [0, 0, 0];
    const dist2 = (p: { x: number; y: number }) => (p.x - listener[0]) * (p.x - listener[0]) + (p.y - listener[2]) * (p.y - listener[2]);
    const walkingPassengers = people.filter(person => g.getTypedUnsafe(AirportPath, person.id).moving)
      .sort((a, b) => dist2(a.position) - dist2(b.position));
    // Four nearby walkers give the concourse texture without building a wall of footsteps.
    for (const person of walkingPassengers.slice(0, 4)) {
      actors.push({ id: person.id, x: person.position.x, y: person.position.y, activity: 'apt-footstep-tile', volume: 0.06 });
    }
    for (const id of staff) {
      const person = g.getTypedUnsafe(AirportStaff, id), position = g.getTypedUnsafe(Transform, id), moving = g.getTypedUnsafe(AirportPath, id).moving;
      actors.push({ id, x: position.x, y: position.y, activity: person.role === 'cleaner' && !moving && !person.onBreak && person.training <= 0 && ids(g, AirportTrash).some(trash => { const litter = g.getTypedUnsafe(Transform, trash); return Math.abs(litter.x - position.x) < 60 && Math.abs(litter.y - position.y) < 60; }) ? 'apt-janitor-mop' : '' });
    }
    const travelers = [
      ...people.map(person => ({ id: person.id, x: person.position.x, y: person.position.y, player: false })),
      ...agents.map(id => { const position = g.getTypedUnsafe(Transform, id); return { id, x: position.x, y: position.y, player: true }; }),
    ];
    const activityCues = [
      ...this.activity.poll(g.frame, desks, actors),
      ...this.detector.poll(SECURITY_GATE, stations(g)[2].unlocked ? travelers : []),
    ];
    if (audio && !LOCAL.muted && !g.paused && !document.hidden) for (const event of activityCues) {
      const handle = audio.play(event.sound, { volume: event.volume, delay: event.delay, position: [event.position.x, 0, event.position.y], spatial: { refDistance: 650, maxDistance: 6000, rolloff: 0.4, air: false } });
      if (event.sourceId !== undefined) { this.typing.get(event.sourceId)?.stop(); this.typing.set(event.sourceId, handle); }
    }
    const next = { collected: e.collected, money: career(g).money, built: stations(g).filter(s => s.unlocked).length, upgrades: e.upgrades, mission: e.mission, hires: e.hires, walkout: e.walkoutUntil, notice: e.noticeUntil, pending: stations(g).reduce((sum, s) => sum + s.pending, 0), flights: e.flights, hoarding: hoardingFor(tier(e).code, stations(g)[6].lanes).length, plan: tier(e).code };
    const cue = (sound: string, position?: { x: number; y: number }, volume = 1.1) => {
      if (!audio || LOCAL.muted || g.paused || document.hidden || g.frame - (this.lastCue.get(sound) ?? -100) < 12) return;
      audio.play(sound, { volume, ...(position ? { position: [position.x, 0, position.y] as const, spatial: { refDistance: 650, maxDistance: 6000, rolloff: 0.4, air: false } } : {}) });
      this.lastCue.set(sound, g.frame);
    };
    if (this.previous) {
      const p = this.previous;
      // Cash can complete the collection lesson in the same tick; its pickup stays a small cue.
      if (next.collected > p.collected) cue('idle-cash-pickup');
      else if (next.mission > p.mission) cue('apt-milestone', undefined, 0.8);
      else if (next.built > p.built) cue('apt-build-complete');
      else if (next.upgrades > p.upgrades) cue('apt-build-upgrade');
      else if (next.hires > p.hires) cue('apt-staff-hired');
      else if (next.walkout > p.walkout) cue('apt-passenger-angry', { x: e.walkoutX, y: e.walkoutY });
      else if (next.money > p.money) cue('idle-cash-pickup');
      else if (next.money < p.money) cue('apt-money-spent');
      else if (next.notice > p.notice) cue('apt-notify');
      // A sale rings the till quietly; the first departure gets the terminal's applause.
      if (next.pending > p.pending) cue('apt-income-tick', undefined, 0.7);
      if (p.flights === 0 && next.flights === 1) cue('apt-applause-small', undefined, 0.9);
      // Hoarding coming down: a run of thuds as the panels land, then the opening chime (timed to src/render/opening.ts).
      if (p.plan === next.plan && next.hoarding < p.hoarding && audio && !LOCAL.muted && !g.paused && !document.hidden) {
        const landed = Math.min(5, p.hoarding - next.hoarding);
        for (let n = 0; n < landed; n++) audio.play('apt-bag-thud', { volume: 0.7, delay: (FALL + n * STAGGER * 3) / 60 });
        audio.play('apt-build-complete', { volume: 1.1, delay: (FALL + 10) / 60 });
      }
    }
    this.previous = next;
    const active = new Set(ids(g, AirportPlane));
    for (const id of active) {
      const plane = g.getTypedUnsafe(AirportPlane, id), previous = this.planes.get(id), position = g.getTypedUnsafe(Transform, id);
      if (previous) {
        if (plane.phase === PHASE.TAXI && previous.phase !== plane.phase) cue('apt-jet-takeoff', position);
        else if (plane.phase === PHASE.APPROACH && previous.phase !== plane.phase) cue('apt-jet-landing', position);
        else if (plane.boardingCalled && !previous.called) cue('apt-pa-chime-3', position);
      }
      this.planes.set(id, { phase: plane.phase, called: plane.boardingCalled });
    }
    for (const id of this.planes.keys()) if (!active.has(id)) this.planes.delete(id);
  };
  cleanup = () => { for (const handle of this.typing.values()) handle.stop(); this.typing.clear(); this.detector.clear(); this.activity.clear(); this.previous = undefined; this.planes.clear(); this.lastCue.clear(); };
}
